import { Component, HostListener, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseService } from '../../base.service';
import { UserAuthService } from '../../auth.service';
import { SPECTRUM_API_PATH, SPECTRUM_AUTH_API } from '../../tokens';
import { SpectrumModule, SpectrumModuleService } from '../module.service';
import { SpectrumSchoolChangeComponent } from '../school-change/school-change.component';
import { SpectrumGuideButtonComponent } from '../../guide/guide-button.component';

/**
 * Shared Spectrum header bar — same markup and stylesheet as the
 * Admissions CRM header (school card, Switch school, profile menu). The CRM
 * keeps its own header because it adds CRM-specific widgets (notification
 * bell, academic-year picker).
 *
 * Modules are switched from the sidebar rail's brand block
 * (`spectrum-sidebar-module-switcher`), not from here.
 */
@Component({
    selector: 'spectrum-header-bar',
    imports: [CommonModule, SpectrumSchoolChangeComponent, SpectrumGuideButtonComponent],
    templateUrl: './header-bar.component.html',
    styleUrl: './header-bar.component.scss'
})
export class SpectrumHeaderBarComponent implements OnInit {

    /** Names the session in the profile menu when the origin matches no module route. */
    @Input() moduleName = '';

    /** Kicker above the org name, e.g. "Current School" / "Current Group". */
    @Input() orgKicker = 'Current School';

    /** Overrides the sessionStorage school name (Group Admin shows its group name). */
    @Input() orgName = '';

    /** Hide the school switcher in apps that aren't school-scoped (Group Admin). */
    @Input() showSchoolSwitch = true;

    private auth = inject(UserAuthService);
    private base = inject(BaseService);
    private apiPath = inject(SPECTRUM_API_PATH);
    private authBase = inject(SPECTRUM_AUTH_API);
    private moduleService = inject(SpectrumModuleService);

    showSchoolChange = false;
    showProfileMenu = false;
    schoolLogoUrl = '';

    /** Only to name the current module in the profile menu — see `sessionLabel`. */
    modules: SpectrumModule[] = [];

    get schoolName(): string {
        return this.orgName || this.auth.getSchoolName() || 'School';
    }

    /**
     * A support-session tab is pinned to the institute the session was started
     * for — switching institutes mid-session is not allowed (and the backend
     * rejects other schools anyway), so the switcher is hidden.
     */
    get schoolSwitchVisible(): boolean {
        return this.showSchoolSwitch && !this.auth.isSupportSession();
    }

    get employeeName(): string {
        return this.auth.getEmployeeName() || 'User';
    }

    get initials(): string {
        return this.employeeName
            .split(' ')
            .filter(Boolean)
            .map(part => part[0])
            .join('')
            .substring(0, 2)
            .toUpperCase() || 'U';
    }

    get currentModule(): SpectrumModule | null {
        return this.moduleService.currentModule(this.modules);
    }

    get sessionLabel(): string {
        return this.currentModule?.moduleName || this.moduleName || 'Spectrum';
    }

    ngOnInit(): void {
        this.loadSchoolLogo();
        this.moduleService.getModules().subscribe(modules => this.modules = modules);
    }

    loadSchoolLogo(): void {
        const params = new URLSearchParams({
            module: 'School',
            fileName: 'logo',
            contentType: 'image/jpeg'
        });
        this.base.get<{ URL?: string; url?: string }>(`${this.apiPath}/documents/get?${params.toString()}`)
            .subscribe({
                next: res => { this.schoolLogoUrl = res?.URL || res?.url || ''; },
                error: () => { this.schoolLogoUrl = ''; }
            });
    }

    handleLogoError(): void {
        this.schoolLogoUrl = '';
    }

    toggleProfileMenu(event: Event): void {
        event.stopPropagation();
        this.showProfileMenu = !this.showProfileMenu;
    }

    @HostListener('document:click')
    closeMenus(): void {
        this.showProfileMenu = false;
    }

    @HostListener('document:keydown.escape')
    closeMenusOnEscape(): void {
        this.closeMenus();
        this.closeSchoolChange();
    }

    openMyProfile(): void {
        this.moduleService.openMyProfile();
    }

    logout(): void {
        this.auth.logout();
        window.location.href = this.authBase;
    }

    openSchoolChange(): void { this.showSchoolChange = true; }
    closeSchoolChange(): void { this.showSchoolChange = false; }
}
